import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem
} from '@mui/material';
import RoleBasedAccess from '../services/RoleBasedAccess';
import { useThemeContext } from '../context/ThemeContext';
import SpiderLogo from './SpiderLogo';
import NavbarActions from './NavbarActions';
import { useLocation } from 'react-router-dom';
import { useSession } from '../context/SessionContext';

const LoggedInNavbar: React.FC = () => {
  const theme = useTheme();
  const { originalUser, handleNavigation } = useSession();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));


  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavMenuItemClick = (path: string) => {
    handleNavigation(path);
    handleMenuClose();
  };

  return (
    <AppBar position="sticky" sx={{ top: originalUser ? '40px' : 0 }}>
      <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box display="flex" alignItems="center">
          <Box
            display="flex"
            alignItems="center"
            // 2. The onClick behavior changes based on screen size
            onClick={isMobile ? handleMenuOpen : () => handleNavigation('/')}
            sx={{ cursor: 'pointer' }} // Always show a pointer to indicate it's clickable
            data-cy={isMobile ? "nav-mobile-menu-trigger" : "nav-logo"}
          >
            <SpiderLogo />
            <Typography variant="h6" color="text.primary" mx={3}>BWI</Typography>
          </Box>
          {isMobile ? (
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
              >
                <RoleBasedAccess allowedRoles={["Internal", "Client", "Client Plus", "Syndication Partner"]}>
                  <MenuItem onClick={() => handleNavMenuItemClick('/assets')} data-cy="nav-mobile-assets-item">Assets</MenuItem>
                </RoleBasedAccess>
                <RoleBasedAccess allowedRoles={["Internal"]}>
                  <MenuItem onClick={() => handleNavMenuItemClick('/reporting')} data-cy="nav-mobile-reporting-item">Reporting</MenuItem>
                </RoleBasedAccess>
                <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                  <MenuItem onClick={() => handleNavMenuItemClick('/asset-queue')} data-cy="nav-mobile-asset-queue-item">Queue</MenuItem>
                </RoleBasedAccess>
                <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                  <MenuItem onClick={() => handleNavMenuItemClick('/systems')} data-cy="nav-mobile-systems-item">Systems</MenuItem>
                </RoleBasedAccess>
                <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                  <MenuItem onClick={() => handleNavMenuItemClick('/layers')} data-cy="nav-mobile-layers-item">Layers</MenuItem>
                </RoleBasedAccess>
                <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                  <MenuItem onClick={() => handleNavMenuItemClick('/overlays')} data-cy="nav-mobile-overlays-item">Overlays</MenuItem>
                </RoleBasedAccess>
              </Menu>
          ) : (
            <>
              <RoleBasedAccess allowedRoles={["Internal", "Client", "Client Plus", "Syndication Partner"]}>
                <Button
                  onClick={() => handleNavigation('/assets')}
                  sx={{
                    color: `${theme.palette.text.primary}`,
                    fontSize: '14px',
                    width: '100px',
                    minHeight: '60px !important',
                    marginX: '5px',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                  data-cy="nav-assets-btn">
                  Assets
                </Button>
              </RoleBasedAccess>
              <RoleBasedAccess allowedRoles={["Internal"]}>
                <Button
                  onClick={() => handleNavigation('/reporting')}
                  sx={{
                    color: `${theme.palette.text.primary}`,
                    fontSize: '14px',
                    width: '100px',
                    minHeight: '60px !important',
                    marginX: '5px',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                  data-cy="nav-reporting-btn">
                  Reporting
                </Button>
              </RoleBasedAccess>
              <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                <Button
                  onClick={() => handleNavigation('/asset-queue')}
                  sx={{
                    color: `${theme.palette.text.primary}`,
                    fontSize: '14px',
                    width: '100px',
                    minHeight: '60px !important',
                    marginX: '5px',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                  data-cy="nav-asset-queue-btn">
                  Queue
                </Button>
              </RoleBasedAccess>
              <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                <Button
                  onClick={() => handleNavigation('/systems')}
                  sx={{
                    color: `${theme.palette.text.primary}`,
                    fontSize: '14px',
                    width: '100px',
                    minHeight: '60px !important',
                    marginX: '5px',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                  data-cy="nav-systems-btn"
                >
                  Systems
                </Button>
              </RoleBasedAccess>
              <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                <Button
                  onClick={() => handleNavigation('/layers')}
                  sx={{
                    color: `${theme.palette.text.primary}`,
                    fontSize: '14px',
                    width: '100px',
                    minHeight: '60px !important',
                    marginX: '5px',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                  data-cy="nav-layers-btn"
                >
                  Layers
                </Button>
              </RoleBasedAccess>
              <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                <Button
                  onClick={() => handleNavigation('/overlays')}
                  sx={{
                    color: `${theme.palette.text.primary}`,
                    fontSize: '14px',
                    width: '100px',
                    minHeight: '60px !important',
                    marginX: '5px',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                  data-cy="nav-overlays-btn"
                >
                  Overlays
                </Button>
              </RoleBasedAccess>
            </>
          )}
        </Box>
        <NavbarActions />
      </Toolbar>
    </AppBar>
  );
};

const LoggedOutNavbar: React.FC = () => {
  const { toggleDarkMode } = useThemeContext();
  return (
    <AppBar position="sticky" /* ...sx styles */>
      <Toolbar sx={{ justifyContent: 'center' }}>
        <SpiderLogo onClick={toggleDarkMode} />
      </Toolbar>
    </AppBar>
  );
};

const MasqueradeBanner = () => {
  const { user, originalUser, stopMasquerade } = useSession();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!originalUser) {
    return null;
  }

  return (
    <AppBar
      position="sticky"
      sx={{
        height: `40px`,
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 1,
        backgroundColor: '#d32f2f',
        color: '#fff',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between', 
        padding: theme.spacing(1, 2),
      }}
    >
      <Typography variant="body2" noWrap sx={{ fontWeight: 'bold' }}>
        {isMobile
          ? `Viewing as: ${user?.email}`
          : `You are currently acting as **${user?.email}** (${user?.role}).`
        }
      </Typography>
      
      {/* The button, also with shorter text for mobile */}
      <Button
        onClick={stopMasquerade}
        variant="contained"
        size="small"
        data-cy="masquerade-exit-btn"
        sx={{
          backgroundColor: '#fff', 
          color: '#d32f2f', 
          '&:hover': { backgroundColor: '#f1f1f1' },
          whiteSpace: 'nowrap',
          marginLeft: 2, 
        }}
      >
        {isMobile ? 'Exit' : `Return to Your Account`}
      </Button>
    </AppBar>
  );
};

const Navbar: React.FC = () => {
  const { isLoggedIn, isLoading } = useSession();
  const location = useLocation();
  const isLogoutPage = location.pathname.includes('/logout');

  if (!isLoggedIn || isLogoutPage) {
    return <LoggedOutNavbar />;
  }

  return (
    <>
      <MasqueradeBanner />
      <LoggedInNavbar />
    </>
  );
};

export default Navbar;
